'use client';

// React Dependencies
import { ReactNode, useEffect, useState } from 'react';

// External Dependencies
import LogRocket from 'logrocket';

// Internal Dependencies
import {
    getDeployEnvironment,
    getLogRocketRootHostname,
    getLogRocketRouting,
    resolveLogRocketPrivacy,
} from '@/lib/config/deploy-environment';
import { ENV } from '@/lib/config/environment';
import { EVENTS, tracking } from '@/lib/features/tracking';

/**
 * Base LogRocket initialization component that doesn't depend on MobX
 */
export function LogRocketInitializer({ children }: { children: ReactNode }) {
    const [isLogRocketReady, setIsLogRocketReady] = useState(false);

    // First useEffect for LogRocket initialization
    useEffect(() => {
        // Only initialize LogRocket if enabled
        if (!ENV.enableLogRocket) {
            console.log('LogRocket is disabled');
            tracking.disable();
            return;
        }

        try {
            // Everything below is decided by the resolved deployment, never by
            // ENV.isDevelopment. That flag is derived from ENVIRONMENT, which is
            // set outside this repo and cannot distinguish test1 from test2 from
            // staging; the hostname can, and is ground truth at the moment init
            // runs (M4S-5593).
            const { environment, source } = getDeployEnvironment();
            const routing = getLogRocketRouting();

            // No project, no LogRocket. There is no default to fall back to:
            // any default is a real project, and every developer machine
            // without the variable would record into it (M4S-5593).
            if (routing.outcome === 'unconfigured') {
                const message =
                    `[LogRocket] not started — NEXT_PUBLIC_LOGROCKET_PROJECT is not set ` +
                    `(environment=${environment} via ${source})`;
                // Expected on a developer machine; a misconfiguration anywhere else.
                if (environment === 'local') console.log(message);
                else console.warn(message);
                tracking.disable();
                return;
            }

            const { appId, outcome } = routing;
            const privacy = resolveLogRocketPrivacy(environment);

            if (source === 'fallback') {
                console.warn(
                    `[LogRocket] No hostname, NODE_ENV or NEXT_PUBLIC_APP_ENV signal matched; ` +
                        `assuming production. Check NEXT_PUBLIC_APP_ENV for this deployment.`,
                );
            }

            LogRocket.init(appId, {
                release: ENV.buildId,
                rootHostname: getLogRocketRootHostname(environment),
                shouldCaptureIP: privacy.shouldCaptureIP,
                shouldDebugLog: privacy.shouldDebugLog,
                console: {
                    shouldAggregateConsoleErrors: true,
                    isEnabled: true,
                },
                network: {
                    isEnabled: true,
                    requestSanitizer: (request) => {
                        if (!privacy.redactNetwork) return request;
                        const sanitizedRequest = { ...request };
                        if (sanitizedRequest.headers['authorization']) {
                            sanitizedRequest.headers['authorization'] = '<<REDACTED>>';
                        }
                        if (sanitizedRequest.headers['url']?.includes('/auth/')) {
                            sanitizedRequest.body = '<<REDACTED>>';
                        }
                        const sensitivePatterns = [
                            /password/i,
                            /token/i,
                            /key/i,
                            /secret/i,
                            /credential/i,
                        ];
                        if (typeof sanitizedRequest.body === 'string') {
                            for (const pattern of sensitivePatterns) {
                                if (pattern.test(sanitizedRequest.body)) {
                                    sanitizedRequest.body = '<<REDACTED>>';
                                    break;
                                }
                            }
                        }
                        return sanitizedRequest;
                    },
                    responseSanitizer: (response) => {
                        if (!privacy.redactNetwork) return response;
                        const sanitizedResponse = { ...response };
                        if (sanitizedResponse.headers['url']?.includes('/auth/')) {
                            sanitizedResponse.body = '<<REDACTED>>';
                        }
                        return sanitizedResponse;
                    },
                },
                dom: {
                    inputSanitizer: privacy.sanitizeInputs,
                    textSanitizer: false, // Only sanitize inputs, not all text
                    baseHref: privacy.baseHref,
                },
            });

            // The SDK holds calls until its recorder script has loaded, so the
            // tracking helper can start sending the moment init returns.
            tracking.init();

            // Wait for LogRocket to be ready
            setTimeout(() => {
                setIsLogRocketReady(true);
                console.log(
                    `[LogRocket] initialised — environment=${environment} (via ${source}), ` +
                        `project=${appId}, routing=${outcome}`,
                );
            }, 1000);
        } catch (error) {
            console.warn('LogRocket initialization failed:', error);
            tracking.disable();
        }
    }, []); // Empty dependency array since this should only run once

    // Third useEffect for performance monitoring - only run when LogRocket is ready
    useEffect(() => {
        if (!isLogRocketReady) return;

        // Same five-value environment the identify() traits carry, so a metric
        // and a session agree about which deployment produced them.
        const { environment } = getDeployEnvironment();

        let memoryInterval: NodeJS.Timeout | null = null;
        let cleanupLongTaskTracking: (() => void) | null = null;
        let lcpObserver: PerformanceObserver | null = null;
        let handleLoad: (() => void) | null = null;

        // Set up performance monitoring if supported
        if ('performance' in window) {
            // Track page load timing
            handleLoad = () => {
                const navigation = performance.getEntriesByType(
                    'navigation',
                )[0] as PerformanceNavigationTiming;
                if (navigation) {
                    tracking.metric(
                        EVENTS.PERFORMANCE.PAGE_LOAD,
                        navigation.loadEventEnd - navigation.startTime,
                        {
                            domComplete: navigation.domComplete,
                            domInteractive: navigation.domInteractive,
                            url: window.location.pathname,
                            environment,
                            buildId: ENV.buildId,
                        },
                    );
                }
            };

            window.addEventListener('load', handleLoad);

            // Track Largest Contentful Paint (LCP)
            lcpObserver = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                const lastEntry = entries[entries.length - 1];
                tracking.metric(EVENTS.PERFORMANCE.LCP, lastEntry.startTime, {
                    url: window.location.pathname,
                    environment,
                    buildId: ENV.buildId,
                });
            });
            lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

            // Track memory usage every minute
            memoryInterval = setInterval(() => {
                tracking.memory();
            }, 60000);

            // Initialize long task tracking
            cleanupLongTaskTracking = tracking.initLongTaskTracking();
        }

        // Cleanup function
        return () => {
            if ('performance' in window) {
                if (handleLoad) window.removeEventListener('load', handleLoad);
                if (lcpObserver) lcpObserver.disconnect();
                if (memoryInterval) clearInterval(memoryInterval);
                if (cleanupLongTaskTracking) cleanupLongTaskTracking();
            }
        };
    }, [isLogRocketReady]); // Only run when LogRocket is ready

    return <>{children}</>;
}

// Set component display names for debugging
LogRocketInitializer.displayName = 'LogRocketInitializer';
